// [Codex 2026-05-27] Adds FileRoom forecasting-model save support.
import ApiClient from "../apiClient";

export interface FileRoomItem {
  id: string;
  sectionId: string;
  deskId: string;
  title: string;
  kind: string;
  path: string;
  summary: string;
  excerpt: string;
  tags: string[];
  tickers: string[];
  sourceRefs: string[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface FileRoomSection {
  id: string;
  title: string;
  description: string;
  items: FileRoomItem[];
}

export interface FileRoomIndex {
  desk: { id: string; name: string };
  root: string;
  sections: FileRoomSection[];
}

export interface FileRoomItemDetail extends FileRoomItem {
  content: string;
}

export interface ChartProofReceipt {
  receiptId?: string | null;
  dedupeId: string;
  symbol: string;
  platformTarget: "tradingview" | "projectx";
  headline: string;
  sourceLabel: string;
  sourceUrl?: string | null;
  eventTimestamp: string;
  bias: "bearish" | "neutral" | "bullish";
  confidence: number;
  severity: number;
  suggestedTimeframe?: string | null;
  suggestedPrice?: number | null;
  visualColorToken?: string | null;
  opacity?: number | null;
  requestedBy: string;
  result: "plotted" | "queued" | "degraded" | "blocked";
  target?: string | null;
  degradedReason?: string | null;
  recordedAt?: string | null;
}

export interface ChartProofResult {
  proofNote: FileRoomItemDetail;
  narrativePreset: {
    title: string;
    query: string;
    catalystIds: string[];
    narrativeSlugs: string[];
    links: Array<{ title: string; url: string; source: string }>;
    color: string;
    deferArtifacts: boolean;
  };
  publicDraft: {
    status: "draft_only";
    channel: "public-proof-draft";
    title: string;
    body: string;
    requiresFinalApproval: true;
    generatedAt: string;
  } | null;
}

export class FileRoomService {
  constructor(private client: ApiClient) {}

  async list(deskId = "priced-in-capital"): Promise<FileRoomIndex> {
    const json = await this.client.get<{ fileRoom: FileRoomIndex }>(
      `/api/file-room?deskId=${encodeURIComponent(deskId)}`,
    );
    return json.fileRoom;
  }

  async getItem(
    id: string,
    deskId = "priced-in-capital",
  ): Promise<FileRoomItemDetail> {
    const params = new URLSearchParams({ id, deskId });
    const json = await this.client.get<{ item: FileRoomItemDetail }>(
      `/api/file-room/item?${params}`,
    );
    return json.item;
  }

  async createProofFromChartReceipt(input: {
    deskId?: string;
    receipt: ChartProofReceipt;
    sourceEvidence?: Array<{
      label: string;
      url?: string | null;
      summary?: string | null;
      sourceType?: string | null;
      riskflowItemId?: string | null;
    }>;
    proofReview?: {
      status: "needs_review" | "approved" | "rejected";
      reviewedBy?: string | null;
      reviewedAt?: string | null;
      notes?: string | null;
    };
  }): Promise<ChartProofResult> {
    const json = await this.client.post<ChartProofResult>(
      "/api/chart-evidence/proofs/from-receipt",
      input,
    );
    return json;
  }

  async saveItem(input: {
    id?: string;
    deskId?: string;
    sectionId: "forecasting-models";
    title: string;
    content: string;
    source?: "manual" | "approved-refinement";
  }): Promise<FileRoomItemDetail> {
    const json = await this.client.put<{ item: FileRoomItemDetail }>(
      "/api/file-room/item",
      {
        deskId: "priced-in-capital",
        source: "manual",
        ...input,
      },
    );
    return json.item;
  }
}
